/**
 * NotificationsSection — per-event email flags (Prompt 11F.8).
 *
 * Backed by `app.core.notifications.config.rules.FLAGS`, which already
 * declares every flag here as tenant-overridable — this section is the
 * write path that module was missing. NOTE: enforcement at the
 * `send_email` call sites is a separate, pre-existing gap (lives in
 * protected `app/core/**`) — toggling a flag off here does not yet stop
 * that email from sending. Documented in `docs/04-modules/settings.md`.
 */

import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, Toggle } from '@/components/common';
import { useTenant } from '@core/tenant/TenantContext';
import {
  useNotificationsConfig,
  useUpdateNotificationsConfig,
  type NotificationsConfig,
} from '@features/settings/api/config';
import type { ApiError } from '@core/api/client';

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleInfo = styled.div``;

const ToggleLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: 2px;
`;

const ReadOnlyBanner = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FLAG_LABELS: Array<{ key: keyof NotificationsConfig['flags']; label: string }> = [
  { key: 'email_enabled', label: 'Email notifications (master switch)' },
  { key: 'email_trial_booking_admin', label: 'Trial booking — notify admin' },
  { key: 'email_booking_confirmation', label: 'Booking confirmation' },
  { key: 'email_invoice_issued', label: 'Invoice issued' },
  { key: 'email_payment_received', label: 'Payment received' },
  { key: 'email_class_cancelled', label: 'Class cancelled' },
];

export const NotificationsSection: React.FC = () => {
  const { isAllMode } = useTenant();
  const { data: config, isLoading } = useNotificationsConfig();
  const updateConfig = useUpdateNotificationsConfig();

  const toggle = async (key: keyof NotificationsConfig['flags']) => {
    const current = config?.flags[key] ?? true;
    try {
      await updateConfig.mutateAsync({ [key]: !current });
      toast.success('Notification settings updated.');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not update notification settings.');
    }
  };

  return (
    <Card padding="lg" data-testid="candeza-settings-section-notifications">
      <h3 style={{ marginTop: 0 }}>Notifications</h3>
      <p style={{ fontSize: 14, color: '#64748b', marginTop: 0 }}>
        Control which automated emails this school sends. The master switch overrides every
        per-event flag below when off.
      </p>

      {isAllMode && (
        <ReadOnlyBanner data-testid="candeza-notifications-all-mode-banner">
          Select a school to edit Notifications.
        </ReadOnlyBanner>
      )}

      {FLAG_LABELS.map(({ key, label }) => (
        <ToggleRow key={key}>
          <ToggleInfo>
            <ToggleLabel>{label}</ToggleLabel>
          </ToggleInfo>
          <Toggle
            active={config?.flags[key] ?? true}
            onToggle={() => toggle(key)}
            disabled={isAllMode || isLoading || updateConfig.isPending}
            data-testid={`candeza-notifications-${key}`}
          />
        </ToggleRow>
      ))}
    </Card>
  );
};

export default NotificationsSection;
